import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api("/auth/forgot", { method: "POST", body: { email } });
      setSent(true);
    } finally { setBusy(false); }
  }
  return (
    <Layout>
      <div className="max-w-md mx-auto avcp-card p-8">
        <h1 className="text-3xl">Forgot password</h1>
        <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">
          We will send a reset link to your email if an account exists.
        </p>
        {sent ? (
          <div className="mt-6 text-sm">
            If an account exists for <span className="font-mono">{email}</span>, a reset email has been sent.
            <div className="mt-4"><Link className="avcp-link" to="/signin">Back to sign in</Link></div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-4">
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">Email</span>
              <input data-testid={TID.forgotEmail} className="avcp-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} />
            </label>
            <button data-testid={TID.forgotSubmit} className="avcp-btn primary" disabled={busy}>
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}

export function Reset() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token") || "";
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  async function submit(e) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api("/auth/reset", { method: "POST", body: { token, new_password: password } });
      setDone(true);
      toast.success("Password reset. You can now sign in.");
    } catch (e) {
      setErr(e?.problem?.detail || e?.problem?.title || e.message);
    } finally { setBusy(false); }
  }
  return (
    <Layout>
      <div className="max-w-md mx-auto avcp-card p-8">
        <h1 className="text-3xl">Reset password</h1>
        {done ? (
          <div className="mt-6 text-sm">
            Password updated. <Link className="avcp-link" to="/signin">Sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-4">
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">Reset token</span>
              <input className="avcp-input font-mono text-xs" required value={token}
                onChange={(e) => setToken(e.target.value)} />
              <span className="text-xs text-[var(--avcp-ink-soft)]">
                Paste the token from your reset email.
              </span>
            </label>
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">New password</span>
              <input data-testid={TID.resetPassword} className="avcp-input" type="password" minLength={12} required
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {err && <div className="text-sm text-[var(--avcp-danger)]">{err}</div>}
            <button data-testid={TID.resetSubmit} className="avcp-btn primary" disabled={busy}>
              {busy ? "Updating…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}

export function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const tokenFromUrl = params.get("token") || "";
  const [token, setToken] = useState(tokenFromUrl);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const autoAttempted = useRef(false);

  const verifyToken = useCallback(async (value, redirectOnSuccess = false) => {
    setBusy(true); setErr(null);
    try {
      await api(`/auth/verify?token=${encodeURIComponent(value)}`);
      await refreshMe();
      setDone(true);
      toast.success("Email verified successfully.");
      if (redirectOnSuccess) navigate("/app", { replace: true });
    } catch (e) {
      setErr(e?.problem?.detail || e?.problem?.title || e.message);
    } finally { setBusy(false); }
  }, [navigate, refreshMe]);

  useEffect(() => {
    if (!tokenFromUrl || autoAttempted.current) return;
    autoAttempted.current = true;
    verifyToken(tokenFromUrl, true);
  }, [tokenFromUrl, verifyToken]);

  async function submit(e) {
    e?.preventDefault?.();
    await verifyToken(token, true);
  }
  return (
    <Layout>
      <div className="max-w-md mx-auto avcp-card p-8">
        <h1 className="text-3xl">Verify email</h1>
        {busy && tokenFromUrl ? (
          <div className="mt-6" role="status" aria-live="polite">
            <div className="text-lg font-medium">Verifying your email…</div>
            <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">
              This will only take a moment.
            </p>
          </div>
        ) : done ? (
          <div className="mt-6">
            <span className="avcp-pill success">Email verified</span>
            <div className="mt-4"><Link className="avcp-link" to="/app" replace>Go to dashboard</Link></div>
          </div>
        ) : tokenFromUrl && err ? (
          <div className="mt-6" role="alert">
            <div className="text-sm text-[var(--avcp-danger)]">{err}</div>
            <p className="mt-3 text-sm text-[var(--avcp-ink-soft)]">
              The link may have expired or already been used. Return to your dashboard to request a new verification email.
            </p>
            <div className="mt-4"><Link className="avcp-btn primary" to="/app" replace>Return to dashboard</Link></div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-4">
            <label className="grid gap-1.5">
              <span className="text-sm font-medium">Verification token</span>
              <input className="avcp-input font-mono text-xs" required value={token}
                onChange={(e) => setToken(e.target.value)} />
              <span className="text-xs text-[var(--avcp-ink-soft)]">
                Open the link in your verification email, or paste the complete token here.
              </span>
            </label>
            {err && <div className="text-sm text-[var(--avcp-danger)]">{err}</div>}
            <button data-testid={TID.verifyAction} className="avcp-btn primary" disabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}
